import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  PetAction,
  ActionCooldown,
  UserPet,
} from '@typings/pets/pet.interface';
import applyActionEffects from '@pages/Pets/store/executeAction';
import Tooltip from '@components/dataDisplay/Tooltip';

interface Props {
  action: PetAction;
  cooldown?: ActionCooldown;
  disabled?: boolean;
  pending?: boolean;
  activePet?: UserPet;
  currentUid?: string;
  setActionCooldowns?: (
    updater: (prev: ActionCooldown[]) => ActionCooldown[],
  ) => void;
}

const ActionButton: React.FC<Props> = ({
  action,
  cooldown,
  disabled = false,
  pending = false,
  activePet,
  currentUid,
  setActionCooldowns,
}) => {
  const [remainingCooldown, setRemainingCooldown] = useState(0);
  const [isExecuting, setIsExecuting] = useState(false);

  // 🕒 쿨다운 계산
  useEffect(() => {
    if (!cooldown) return;

    const updateCooldown = () => {
      const now = new Date();
      const lastUsed = new Date(cooldown.lastUsed);
      const timeDiff = now.getTime() - lastUsed.getTime();
      const cooldownMs = cooldown.cooldownMinutes * 60 * 1000;
      const remaining = Math.max(0, cooldownMs - timeDiff);

      setRemainingCooldown(remaining);
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);

    return () => clearInterval(interval);
  }, [cooldown]);

  // 🎯 액션 실행
  const handleClick = async () => {
    if (disabled || remainingCooldown > 0 || isExecuting || !action.isEnabled) {
      return;
    }

    const hungerCost = action.effects.hungerDecrease ?? 0;
    const currentHunger = activePet?.hunger ?? 0;
    if (hungerCost > 0 && hungerCost > currentHunger) {
      alert(`배고픔이 부족합니다.\n필요 배고픔: ${hungerCost} / 현재 배고픔: ${currentHunger}`);
      return;
    }

    setIsExecuting(true);
    try {
      await applyActionEffects({
        action,
        activePet,
        currentUid,
        setActionCooldowns,
      });
    } catch (error) {
      console.error('액션 실행 실패:', error);
    } finally {
      setIsExecuting(false);
    }
  };

  // 쿨다운 시간 포맷팅
  const formatCooldown = (ms: number) => {
    const seconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}시간 ${minutes % 60}분`;
    } else if (minutes > 0) {
      return `${minutes}분 ${seconds % 60}초`;
    } else {
      return `${seconds}초`;
    }
  };

  const isOnCooldown = remainingCooldown > 0;
  const isDisabled = disabled || !action.isEnabled || isOnCooldown;
  const showPending = pending || isExecuting;
  const tooltipText = isOnCooldown
    ? `남은 쿨타임 ${formatCooldown(remainingCooldown)}`
    : action.cooldown
      ? `쿨타임 ${action.cooldown}분`
      : '즉시 사용 가능';

  return (
    <Container>
      <Tooltip
        message={
          <>
            <span style={{ fontSize: 10 }}>{tooltipText}</span>

            {action.effects.triggerInteraction && (
              <Effect>🤝 상호작용 문구</Effect>
            )}

            {action.effects.petExp && (
              <Effect>✨ +{action.effects.petExp}</Effect>
            )}
            {action.effects.currency && (
              <Effect>💰 +{action.effects.currency.amount}</Effect>
            )}
            {action.effects.hungerDecrease && (
              <Effect>🍽️ -{action.effects.hungerDecrease}</Effect>
            )}
          </>
        }
        direction="bottom"
      >
        <Button
          onClick={handleClick}
          disabled={isDisabled}
          $color={action.color}
          $isDisabled={isDisabled}
          $isOnCooldown={isOnCooldown}
          $isPending={showPending}
        >
          <ButtonContent>
            <ActionIcon>{action.icon}</ActionIcon>
            <ActionName>{action.name}</ActionName>

            {showPending && <LoadingSpinner />}

            {isOnCooldown && (
              <CooldownOverlay>
                <CooldownTimer>
                  {formatCooldown(remainingCooldown)}
                </CooldownTimer>
              </CooldownOverlay>
            )}
          </ButtonContent>

          {/* 효과 미리보기 */}
        </Button>
      </Tooltip>

      {!action.isEnabled && <DisabledLabel>비활성화됨</DisabledLabel>}
    </Container>
  );
};

// 🎨 스타일 컴포넌트
const Container = styled.div`
  position: relative;
  flex: 1;
  margin: 0 4px;

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }
`;

const Button = styled.button<{
  $color: string;
  $isDisabled: boolean;
  $isOnCooldown: boolean;
  $isPending: boolean;
}>`
  position: relative;
  width: 100%;
  height: 40px;
  background: ${({ $color }) => `${$color}`};
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 12px;
  font-weight: 500;
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  overflow: hidden;

  transition: all 0.2s ease;

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    filter: saturate(70%);
    opacity: 0.8;
  `}

  ${({ $isDisabled, $isOnCooldown }) =>
    !$isDisabled &&
    !$isOnCooldown &&
    `
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
    
    &:active {
      transform: translateY(0);
    }
  `}
  
  ${({ $isPending }) =>
    $isPending &&
    `
    opacity: 0.7;
    cursor: wait;
  `}
  
  ${({ $isOnCooldown }) =>
    $isOnCooldown &&
    `
    opacity: 0.5;
    cursor: not-allowed;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: repeating-linear-gradient(
        45deg,
        transparent,
        transparent 2px,
        rgba(0, 0, 0, 0.1) 2px,
        rgba(0, 0, 0, 0.1) 4px
      );
    }
  `}
`;

const ButtonContent = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 8px;
`;

const ActionIcon = styled.div`
  font-size: 24px;
  margin-bottom: 4px;
`;

const ActionName = styled.div`
  font-size: 11px;
  text-align: center;
  line-height: 1.2;
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
`;

const CooldownOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
`;

const CooldownTimer = styled.div`
  font-size: 10px;
  font-weight: bold;
  text-align: center;
  line-height: 1.2;
`;

const EffectPreview = styled.div`
  position: absolute;
  bottom: -25px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;

  ${Button}:hover & {
    opacity: 1;
  }
`;

const Effect = styled.div`
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  white-space: nowrap;
`;

const DisabledLabel = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  background: #f44336;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  white-space: nowrap;
`;

export default ActionButton;
